<template>
  <div class="orangehrm-background-container balance-emails">
    <section
      class="orangehrm-card-container"
      aria-labelledby="balance-emails-title"
    >
      <div class="balance-emails-heading">
        <oxd-text
          id="balance-emails-title"
          tag="h6"
          class="orangehrm-main-title"
        >
          Balance Emails
        </oxd-text>
        <span v-if="hasLoaded" class="balance-emails-status" role="status">
          Saved status: {{ savedConfiguration.enabled ? 'On' : 'Off' }}
        </span>
      </div>
      <oxd-text tag="p" class="balance-emails-description">
        Send each eligible staff member a quarterly email with their own
        remaining vacation and sick hours.
      </oxd-text>
      <oxd-text v-if="loadingOptions" tag="p" role="status">
        Loading email settings…
      </oxd-text>
      <div v-if="optionsError" class="balance-emails-error" role="alert">
        <oxd-text tag="p">{{ optionsError }}</oxd-text>
        <oxd-button
          v-if="!hasLoaded"
          type="button"
          display-type="ghost"
          label="Retry loading settings"
          :disabled="loadingOptions"
          @click="loadOptions"
        />
      </div>
      <template v-if="hasLoaded">
        <div class="balance-emails-schedule">
          <oxd-text tag="p">{{ schedule.description }}</oxd-text>
          <oxd-text tag="p">
            Next scheduled date: <strong>{{ nextRunLabel }}</strong>
          </oxd-text>
          <oxd-text v-if="!savedConfiguration.enabled" tag="p">
            Automatic emails are off. No email will be sent unless you enable
            this setting and save it.
          </oxd-text>
        </div>
        <div v-if="savedConfiguration.enabled" class="balance-emails-actions">
          <oxd-button
            type="button"
            display-type="ghost"
            :label="turningOff ? 'Turning off…' : 'Turn off now'"
            :disabled="!canTurnOff"
            @click="turnOffNow"
          />
          <oxd-text tag="p">
            Stop future scheduled emails without saving your message edits.
          </oxd-text>
        </div>
        <oxd-text
          v-if="actionMessage"
          tag="p"
          role="status"
          class="balance-emails-description"
        >
          {{ actionMessage }}
        </oxd-text>
        <oxd-text
          v-if="configurationWarning"
          tag="p"
          class="balance-emails-warning"
          role="status"
        >
          {{ configurationWarning }}
        </oxd-text>
        <oxd-text
          v-if="schedule.warning"
          tag="p"
          class="balance-emails-warning"
          role="status"
        >
          {{ schedule.warning }}
        </oxd-text>
        <oxd-text
          v-if="!mailConfigured"
          tag="p"
          class="balance-emails-warning"
          role="status"
        >
          Email is not configured. You can edit and preview the message, but
          automatic emails require a working mail configuration.
        </oxd-text>
        <oxd-text
          v-if="!deliveryEnvironmentReady"
          tag="p"
          class="balance-emails-warning"
          role="status"
        >
          This environment supports settings and previews only. Scheduled
          balance emails can run only in production.
        </oxd-text>
        <oxd-divider />
        <oxd-input-field
          v-model="configuration.enabled"
          type="switch"
          label="Enable quarterly balance emails"
          :disabled="settingsBusy"
        />
        <oxd-text tag="p" class="balance-emails-description">
          Review the message preview before enabling. Changes, including this
          switch, take effect only when you select Save.
        </oxd-text>
        <oxd-input-field
          v-model="configuration.subjectTemplate"
          label="Email subject"
          :maxlength="200"
          :disabled="settingsBusy"
          required
          @focusin="insertionTarget = 'subjectTemplate'"
        />
        <oxd-input-field
          v-model="configuration.messageTemplate"
          type="textarea"
          label="Email message (plain text)"
          :maxlength="10000"
          class="balance-emails-message-input"
          :disabled="settingsBusy"
          required
          @focusin="insertionTarget = 'messageTemplate'"
        />
        <oxd-text tag="p" class="balance-emails-description">
          Keep both balance placeholders in the message. Each email uses only
          its recipient's figures. Formatting and HTML are treated as plain
          text.
        </oxd-text>
        <div class="balance-emails-token-tools">
          <label for="balance-email-token-target">Add a placeholder to</label>
          <select
            id="balance-email-token-target"
            v-model="insertionTarget"
            :disabled="settingsBusy"
          >
            <option value="messageTemplate">Message</option>
            <option value="subjectTemplate">Subject</option>
          </select>
        </div>
        <ul class="balance-emails-tokens" aria-label="Available placeholders">
          <li v-for="token in availableTokens" :key="token">
            <button
              type="button"
              :disabled="settingsBusy"
              :aria-label="`Add ${token} to ${
                insertionTarget === 'subjectTemplate' ? 'subject' : 'message'
              }`"
              @click="insertToken(token)"
            >
              {{ token }}
            </button>
            <span>{{ tokenDescription(token) }}</span>
          </li>
        </ul>
        <oxd-text
          v-if="validationError"
          tag="p"
          class="balance-emails-warning"
          role="status"
        >
          {{ validationError }}
        </oxd-text>
        <div class="balance-emails-actions">
          <oxd-button
            type="button"
            display-type="secondary"
            :label="saving ? 'Saving…' : 'Save'"
            :disabled="!canSave"
            @click="saveConfiguration"
          />
          <oxd-text tag="p" role="status" aria-live="polite">
            {{
              dirty ? 'Unsaved changes' : saveMessage || 'Settings are saved.'
            }}
          </oxd-text>
        </div>
      </template>
    </section>

    <section
      v-if="hasLoaded"
      class="orangehrm-card-container balance-emails-section"
      aria-labelledby="balance-emails-preview-title"
    >
      <oxd-text id="balance-emails-preview-title" tag="h6">
        Review the message
      </oxd-text>
      <oxd-text tag="p" class="balance-emails-description">
        Preview uses your current subject and message, including unsaved edits,
        and the selected employee's current balances. Preview does not send
        email or save changes.
      </oxd-text>
      <oxd-text tag="p" class="balance-emails-description">
        {{ eligibilitySummary }}
      </oxd-text>
      <oxd-text tag="p" class="balance-emails-description">
        Only eligible full-time staff with at least one posted vacation or sick
        bank are included. Part-time staff are excluded. An unallocated benefit
        is shown as “Not allocated.” Recipients are checked again when the
        scheduled run starts.
      </oxd-text>
      <oxd-input-field
        v-model="selectedRecipient"
        type="select"
        label="Employee to preview"
        :options="recipientOptions"
        :disabled="settingsBusy || !recipientOptions.length"
      />
      <oxd-text
        v-if="!recipientOptions.length"
        tag="p"
        class="balance-emails-warning"
      >
        No employees are currently eligible for a preview.
      </oxd-text>
      <details v-if="excluded.length" class="balance-emails-excluded">
        <summary>
          {{ excluded.length }} employees excluded from this run
        </summary>
        <ul>
          <li v-for="(employee, index) in excluded" :key="index">
            {{ employee.name }} — {{ employee.reason }}
          </li>
        </ul>
      </details>
      <div class="balance-emails-actions">
        <oxd-button
          type="button"
          display-type="secondary"
          :label="generating ? 'Generating preview…' : 'Preview message'"
          :disabled="!canPreview"
          @click="generatePreview"
        />
        <oxd-text v-if="generating" tag="p" role="status">
          Preparing a preview. No email is being sent.
        </oxd-text>
      </div>
      <oxd-text
        v-if="previewError"
        tag="p"
        class="balance-emails-error"
        role="alert"
      >
        {{ previewError }}
      </oxd-text>
      <oxd-text
        v-if="previewNotice"
        tag="p"
        class="balance-emails-description"
        role="status"
      >
        {{ previewNotice }}
      </oxd-text>
      <article
        v-if="preview"
        class="balance-emails-preview"
        aria-label="Email preview"
      >
        <dl>
          <dt>To</dt>
          <dd>
            {{ preview.recipient.name }} &lt;{{ preview.recipient.email }}&gt;
          </dd>
          <dt>Subject</dt>
          <dd>{{ preview.subject }}</dd>
          <dt>Balances as of</dt>
          <dd>{{ preview.asOfDate }}</dd>
        </dl>
        <pre class="balance-emails-preview-body">{{ preview.body }}</pre>
        <oxd-text tag="p" class="balance-emails-description">
          Preview only. The scheduled email recalculates balances at the time of
          the run.
        </oxd-text>
      </article>
    </section>

    <section
      class="orangehrm-card-container balance-emails-section"
      aria-labelledby="balance-emails-history-title"
    >
      <div class="balance-emails-heading">
        <oxd-text id="balance-emails-history-title" tag="h6"
          >Delivery history</oxd-text
        >
        <oxd-button
          type="button"
          display-type="ghost"
          label="Refresh history"
          :disabled="loadingHistory"
          @click="loadHistory"
        />
      </div>
      <oxd-text tag="p" class="balance-emails-description">
        Accepted means the mail provider accepted the email; it does not confirm
        inbox delivery. History does not include message bodies or leave
        balances.
      </oxd-text>
      <oxd-text v-if="loadingHistory" tag="p" role="status"
        >Loading delivery history…</oxd-text
      >
      <oxd-text
        v-if="historyError"
        tag="p"
        class="balance-emails-error"
        role="alert"
      >
        {{ historyError }}
      </oxd-text>
      <oxd-text
        v-if="!loadingHistory && !historyError && !deliveries.length"
        tag="p"
      >
        No scheduled balance emails are recorded.
      </oxd-text>
      <div
        v-if="deliveries.length"
        class="balance-emails-table-scroll"
        tabindex="0"
        aria-label="Delivery history"
      >
        <table class="balance-emails-table">
          <caption>
            Recent quarterly balance email attempts. Times are shown in Eastern
            time.
          </caption>
          <thead>
            <tr>
              <th scope="col">Quarter</th>
              <th scope="col">Staff member</th>
              <th scope="col">Email</th>
              <th scope="col">Status</th>
              <th scope="col">Details</th>
              <th scope="col">Updated (Eastern)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="delivery in deliveries" :key="delivery.id">
              <td>{{ delivery.quarter }}</td>
              <th scope="row">{{ delivery.name }}</th>
              <td>{{ delivery.email || 'Unavailable' }}</td>
              <td>{{ deliveryStatus(delivery.status) }}</td>
              <td>{{ delivery.reason || '—' }}</td>
              <td>
                {{ easternDate(delivery.updatedAt || delivery.createdAt) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';

const settingsPath = '/api/v2/leave/quarterly-balance-emails';
const tokenHelp = {
  first_name: 'First name',
  employee_name: 'Full name',
  vacation_hours: 'Remaining vacation hours (required)',
  sick_hours: 'Remaining sick hours (required)',
  as_of_date: 'Balance date',
  quarter: 'Calendar quarter',
  year: 'Year',
  leave_url: 'Link to Leave',
};

export default {
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, settingsPath);
    http.setIgnorePath(settingsPath);
    return {http};
  },
  data() {
    return {
      hasLoaded: false,
      loadingOptions: true,
      loadingHistory: false,
      saving: false,
      turningOff: false,
      generating: false,
      configuration: {enabled: false, subjectTemplate: '', messageTemplate: ''},
      savedConfiguration: {
        enabled: false,
        subjectTemplate: '',
        messageTemplate: '',
      },
      schedule: {
        description: '',
        timezone: 'America/New_York',
        nextRunAt: null,
      },
      mailConfigured: false,
      deliveryEnvironmentReady: false,
      tokens: [],
      recipients: [],
      excluded: [],
      eligibilitySummary: '',
      selectedRecipient: null,
      insertionTarget: 'messageTemplate',
      preview: null,
      previewVersion: 0,
      previewNotice: '',
      deliveries: [],
      optionsError: '',
      previewError: '',
      historyError: '',
      saveMessage: '',
      actionMessage: '',
      configurationWarning: '',
    };
  },
  computed: {
    settingsBusy() {
      return this.saving || this.turningOff;
    },
    canTurnOff() {
      return (
        this.hasLoaded &&
        this.savedConfiguration.enabled &&
        !this.loadingOptions &&
        !this.settingsBusy
      );
    },
    dirty() {
      return ['enabled', 'subjectTemplate', 'messageTemplate'].some(
        (key) => this.configuration[key] !== this.savedConfiguration[key],
      );
    },
    validationError() {
      if (!this.configuration.subjectTemplate.trim())
        return 'Enter an email subject.';
      if (!this.configuration.messageTemplate.trim())
        return 'Enter an email message.';
      if (
        this.configuration.subjectTemplate.length > 200 ||
        /[\r\n]/.test(this.configuration.subjectTemplate)
      ) {
        return 'Keep the subject to one line and no more than 200 characters.';
      }
      if (this.configuration.messageTemplate.length > 10000) {
        return 'Keep the message to no more than 10,000 characters.';
      }
      if (
        !['{{vacation_hours}}', '{{sick_hours}}'].every((token) =>
          this.configuration.messageTemplate.includes(token),
        )
      ) {
        return 'Keep {{vacation_hours}} and {{sick_hours}} in the message so both balances are included.';
      }
      return '';
    },
    canSave() {
      return (
        this.hasLoaded &&
        !this.loadingOptions &&
        !this.settingsBusy &&
        this.dirty &&
        !this.validationError
      );
    },
    canPreview() {
      return (
        this.hasLoaded &&
        !this.loadingOptions &&
        !this.settingsBusy &&
        !this.generating &&
        Boolean(this.selectedRecipient) &&
        !this.validationError
      );
    },
    recipientOptions() {
      return this.recipients.map((recipient) => ({
        id: recipient.empNumber,
        label: `${recipient.name} — ${recipient.email}`,
      }));
    },
    availableTokens() {
      return this.tokens.map((token) =>
        token.startsWith('{{') ? token : `{{${token}}}`,
      );
    },
    previewFingerprint() {
      return JSON.stringify([
        this.configuration.subjectTemplate,
        this.configuration.messageTemplate,
        this.selectedRecipient?.id,
      ]);
    },
    nextRunLabel() {
      return this.schedule.nextRunAt
        ? this.easternDate(this.schedule.nextRunAt)
        : 'Not scheduled';
    },
  },
  watch: {
    previewFingerprint() {
      this.invalidatePreview();
    },
    dirty(value) {
      if (value) this.saveMessage = '';
    },
  },
  beforeMount() {
    this.loadOptions();
    this.loadHistory();
  },
  beforeUnmount() {
    this.previewVersion++;
  },
  methods: {
    errorMessage(error, fallback) {
      const message =
        error?.response?.data?.error?.message || error?.data?.error?.message;
      return typeof message === 'string' && message.trim() ? message : fallback;
    },
    applyOptions(options) {
      this.invalidatePreview();
      this.configuration = {
        enabled: options.configuration.enabled === true,
        subjectTemplate: options.configuration.subjectTemplate,
        messageTemplate: options.configuration.messageTemplate,
      };
      this.savedConfiguration = {...this.configuration};
      this.schedule = options.schedule;
      this.tokens = options.tokens;
      this.mailConfigured = options.mailConfigured;
      this.deliveryEnvironmentReady = options.deliveryEnvironmentReady === true;
      this.recipients = options.recipients;
      this.excluded = options.excluded;
      this.eligibilitySummary = options.eligibilitySummary;
      this.configurationWarning = options.configurationWarning || '';
      if (
        !this.recipients.some(
          (recipient) => recipient.empNumber === this.selectedRecipient?.id,
        )
      ) {
        this.selectedRecipient = null;
      }
      this.hasLoaded = true;
    },
    async loadOptions() {
      this.loadingOptions = true;
      this.optionsError = '';
      try {
        const {data} = await this.http.getAll();
        this.applyOptions(data.data);
      } catch (error) {
        this.optionsError = this.errorMessage(
          error,
          'Unable to load email settings. Try again.',
        );
      } finally {
        this.loadingOptions = false;
      }
    },
    async saveConfiguration() {
      if (!this.canSave) return;
      this.saving = true;
      this.optionsError = '';
      this.saveMessage = '';
      this.actionMessage = '';
      try {
        const {data} = await this.http.request({
          method: 'PUT',
          data: {
            enabled: this.configuration.enabled,
            subjectTemplate: this.configuration.subjectTemplate,
            messageTemplate: this.configuration.messageTemplate,
          },
        });
        this.applyOptions(data.data);
        this.saveMessage = 'Settings saved.';
      } catch (error) {
        this.optionsError = this.errorMessage(
          error,
          'Unable to confirm that settings were saved. Your edits are retained; retry Save to apply them.',
        );
      } finally {
        this.saving = false;
      }
    },
    async turnOffNow() {
      if (!this.canTurnOff) return;
      this.turningOff = true;
      this.optionsError = '';
      this.actionMessage = '';
      const draft = {
        subjectTemplate: this.configuration.subjectTemplate,
        messageTemplate: this.configuration.messageTemplate,
      };
      try {
        const {data} = await this.http.request({
          method: 'PUT',
          data: {enabled: false},
        });
        if (data.data.configuration.enabled !== false) {
          throw new Error('The server did not confirm the disabled setting.');
        }
        this.applyOptions(data.data);
        this.configuration = {...this.configuration, ...draft, enabled: false};
        this.actionMessage = this.dirty
          ? 'Automatic emails are off. Your unsaved message edits were kept.'
          : 'Automatic emails are off.';
      } catch (error) {
        this.optionsError = this.errorMessage(
          error,
          'Unable to confirm that automatic emails are off. Retry Turn off now. Your message edits are retained.',
        );
      } finally {
        this.turningOff = false;
      }
    },
    tokenDescription(token) {
      return tokenHelp[token.replace(/^\{\{|\}\}$/g, '')] || '';
    },
    insertToken(token) {
      if (this.settingsBusy || !this.availableTokens.includes(token)) return;
      const current = this.configuration[this.insertionTarget];
      this.configuration[this.insertionTarget] =
        current + (current && !/\s$/.test(current) ? ' ' : '') + token;
    },
    invalidatePreview() {
      if (this.preview || this.generating) {
        this.previewNotice =
          'The message or employee changed. Generate a new preview to review the current values.';
      }
      this.previewVersion++;
      this.preview = null;
      this.previewError = '';
    },
    async generatePreview() {
      if (!this.canPreview) return;
      this.generating = true;
      this.preview = null;
      this.previewError = '';
      this.previewNotice = '';
      const version = ++this.previewVersion;
      const fingerprint = this.previewFingerprint;
      try {
        const {data} = await this.http.create({
          subjectTemplate: this.configuration.subjectTemplate,
          messageTemplate: this.configuration.messageTemplate,
          empNumber: this.selectedRecipient.id,
        });
        if (
          version === this.previewVersion &&
          fingerprint === this.previewFingerprint
        ) {
          this.preview = data.data;
        }
      } catch (error) {
        if (
          version === this.previewVersion &&
          fingerprint === this.previewFingerprint
        ) {
          this.previewError = this.errorMessage(
            error,
            'Unable to preview this message. Check the employee and templates, then try again.',
          );
        }
      } finally {
        this.generating = false;
      }
    },
    async loadHistory() {
      if (this.loadingHistory) return;
      this.loadingHistory = true;
      this.historyError = '';
      try {
        const {data} = await this.http.getAll({view: 'history'});
        this.deliveries = data.data.deliveries;
      } catch (error) {
        this.historyError = this.errorMessage(
          error,
          'Unable to load delivery history. Select Refresh history to try again.',
        );
      } finally {
        this.loadingHistory = false;
      }
    },
    easternDate(value) {
      if (!value) return 'Unavailable';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Unavailable';
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(date);
    },
    deliveryStatus(status) {
      return (
        {
          accepted: 'Accepted by mail provider',
          skipped: 'Skipped',
          failed: 'Failed',
          sending: 'Acceptance unconfirmed',
        }[status] || 'Unknown'
      );
    },
  },
};
</script>

<style src="./quarterly-leave-balance-emails.scss" lang="scss" scoped></style>
